import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { routePaths } from 'router/route-paths';
import { useGetAdditionalCosts } from 'api/additional-cost/use-get-additional-costs';
import type { TAdditionalCostRes, TAdditionalCostType, TripRes, UserRes } from 'api/swagger';
import { useRoleGuard, UserRole } from 'shared/hooks/use-role-guard';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { AdditionalCostType } from 'types/additional-cost';
import { CURRENCY_SIGN, ECurrency } from 'types/currencies';
import { AdditionalCostControl } from 'components/common/additional-cost-control';
import { HiddenData } from 'components/hidden-data';
import type { IOrderAdditionalCostsProps } from './types';

export const OrderAdditionalCosts = (props: IOrderAdditionalCostsProps) => {
  const { orderId } = props;
  const { t } = useTranslation('additional-cost');

  const { data: additionalCosts, isLoading } = useGetAdditionalCosts({ orderId });

  const isAllowFinancialInfo = useRoleGuard([
    UserRole.admin,
    UserRole.assistant,
    UserRole.transport_manager,
    UserRole.customer_manager,
    UserRole.storekeeper,
    UserRole.accountant,
  ]);

  const tableData = useTransformToTableData(additionalCosts || []);

  const columns: ColumnsType<TAdditionalCostRes> = [
    {
      title: t('type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: TAdditionalCostType) => {
        return <Tag>{AdditionalCostType[type]}</Tag>;
      },
    },
    {
      title: t('cost'),
      dataIndex: 'value',
      key: 'value',
      render: (value: number, { valueInDefault, currency }: TAdditionalCostRes) => {
        if (!isAllowFinancialInfo) return <HiddenData />;

        const isDefaultCurrency = currency === ECurrency.EUR;
        return (
          <>
            <div>
              {isDefaultCurrency
                ? `${value} ${CURRENCY_SIGN[ECurrency.EUR]}`
                : `≈ ${valueInDefault} ${CURRENCY_SIGN[ECurrency.EUR]}`}
            </div>
            {!isDefaultCurrency ? (
              <div>
                <Typography.Text type='secondary'>
                  {value} {CURRENCY_SIGN[currency]}
                </Typography.Text>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      title: t('user'),
      dataIndex: 'user',
      key: 'user',
      render: (user: UserRes) => {
        return user.name;
      },
    },
    {
      title: t('trip'),
      dataIndex: 'trip',
      key: 'trip',
      render: (trip: TripRes) => {
        if (!trip) return '–';
        return <Link to={generatePath(routePaths.trip, { tripId: String(trip.id) })}>{trip.compositeId}</Link>;
      },
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => {
        return <AdditionalCostControl orderId={orderId} additionalCost={record} />;
      },
    },
  ];

  return <Table loading={isLoading} columns={columns} dataSource={tableData} pagination={false} />;
};
